// src/features/billing/GrantCreditModal.tsx
// Grants credit to a billing account (CreateCreditGrant). Amount is entered in
// major units (e.g. "50.00") and converted to minor units on submit, same
// convention as src/pages/payment/PaymentsLedgerPage.tsx.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input } from '@react/components';
import { FormDialog } from '@/components/form';
import { errMessage } from './format';

export interface GrantCreditValue {
  amount: string;
  currency: string;
  reason: string;
  expiresAt: string; // yyyy-mm-dd, optional
}

export interface GrantCreditModalProps {
  open: boolean;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (value: GrantCreditValue) => Promise<void>;
}

const EMPTY: GrantCreditValue = { amount: '', currency: 'USD', reason: '', expiresAt: '' };

/** A valid, non-negative money amount like "50", "50.00", or "" (cleared). */
function isValidAmount(value: string): boolean {
  if (!value.trim()) return false;
  return /^\d+(\.\d{1,2})?$/.test(value.trim());
}

export function GrantCreditModal({ open, isSaving, onCancel, onSubmit }: GrantCreditModalProps) {
  const { t } = useI18n();
  const [form, setForm] = useState<GrantCreditValue>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const change = (field: keyof GrantCreditValue, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const dirty = !!(form.amount.trim() || form.reason.trim());
  const amountValid = isValidAmount(form.amount);
  const canSubmit = amountValid && !!form.currency.trim() && !!form.reason.trim();

  const close = () => {
    setForm(EMPTY);
    setTouched(false);
    onCancel();
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setError(null);
    try {
      await onSubmit(form);
      setForm(EMPTY);
      setTouched(false);
    } catch (err) {
      setError(errMessage(err, t('billing.creditGrants.createError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={handleSubmit}
      submitLabel={t('billing.creditGrants.grant')}
      canSubmit={canSubmit}
      isSubmitting={isSaving}
      dirty={dirty}
      aria-label={t('billing.creditGrants.grant')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        <h3 className="text-base font-semibold text-foreground">{t('billing.creditGrants.grant')}</h3>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('billing.creditGrants.amount')}
            type="text"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => change('amount', e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="50.00"
            className="font-mono"
            error={touched && !amountValid ? t('billing.creditGrants.amountInvalid') : undefined}
          />
          <Input
            label={t('billing.creditGrants.currency')}
            value={form.currency}
            onChange={(e) => change('currency', e.target.value.toUpperCase())}
            placeholder="USD"
            className="font-mono"
          />
        </div>
        <Input label={t('billing.creditGrants.reason')} value={form.reason} onChange={(e) => change('reason', e.target.value)} />
        <Input
          label={t('billing.creditGrants.expiresAt')}
          type="date"
          value={form.expiresAt}
          onChange={(e) => change('expiresAt', e.target.value)}
          helperText={t('billing.creditGrants.expiresAtHint')}
        />
      </div>
    </FormDialog>
  );
}

export default GrantCreditModal;
